import { createFileRoute } from "@tanstack/react-router";

import { MainLayout } from "@/components/layouts/main-layout";
import { Skeleton } from "@/components/ui/skeleton";
import { IncomeExpenseChart } from "@/features/dashboard/charts/income-expense-chart";
import { TopCustomersChart } from "@/features/dashboard/charts/top-customers-chart";
import { TopSuppliersChart } from "@/features/dashboard/charts/top-suppliers-chart";
import { DashboardFilters } from "@/features/dashboard/dashboard-filters";
import type { TimeGranularity } from "@/lib/ledger/dashboard-analytics";
import { getYTDDateRange } from "@/lib/utils";
import { useGetCompany } from "@/queries/companies/fetch";
import { useGetAllInvoices } from "@/queries/invoices/get-all";
import { useGetDashboardAggregations } from "@/queries/ledger/get-dashboard-aggregations";
import { useEffect, useMemo, useState } from "react";
import type { DateRange } from "react-day-picker";

export const Route = createFileRoute(
  "/_authenticated/$company/unternehmen/reporting/dashboard/",
)({
  component: DashboardPage,
});

function DashboardPage() {
  const { companyData, companyLoading } = useGetCompany();

  const [dateRange, setDateRange] = useState<DateRange | null>(null);
  const [granularity, setGranularity] = useState<TimeGranularity>("monthly");

  useEffect(() => {
    if (companyData?.company && !dateRange) {
      const ytdRange = getYTDDateRange(
        companyData.company.fiscalYearStartMonth,
        companyData.company.fiscalYearStartDay,
      );
      setDateRange(ytdRange);
    }
  }, [companyData, dateRange]);

  const {
    data: aggregations,
    loading,
    error,
  } = useGetDashboardAggregations({
    startDate: dateRange?.from,
    endDate: dateRange?.to,
    granularity,
    topLimit: 10,
  });

  const timeSeriesData = aggregations?.timeSeriesByAccount || [];
  const timeSeriesCounterpartiesData =
    aggregations?.timeSeriesByCounterparty || [];
  const topCustomersData = aggregations?.topCustomers || [];
  const topSuppliersData = aggregations?.topSuppliers || [];

  // Fetch unpaid invoices for receivables/liabilities
  const { data: invoicesData } = useGetAllInvoices({
    query: {
      isPaid: { value: false, op: "eq" },
    },
    page: 1,
    limit: 1000,
  });

  // Calculate receivables and liabilities metrics
  const invoiceMetrics = useMemo(() => {
    const now = new Date();

    const receivablesTotal = 0;
    const receivablesOpen = 0;
    const receivablesOverdue = 0;
    let liabilitiesTotal = 0;
    let liabilitiesOpen = 0;
    let liabilitiesOverdue = 0;

    if (invoicesData && Array.isArray(invoicesData)) {
      invoicesData.forEach((invoiceItem) => {
        const invoice = invoiceItem.data?.invoice;
        if (!invoice || !invoice.totalAmount) return;

        const amount = invoice.totalAmount;
        const isOverdue = invoice.dueDate && new Date(invoice.dueDate) < now;

        // Note: Adjust type detection based on your schema
        // For now, treating all unpaid invoices as liabilities (incoming invoices to pay)
        // You may need to add a field to distinguish receivables vs liabilities
        liabilitiesTotal += amount;
        if (isOverdue) {
          liabilitiesOverdue += amount;
        } else {
          liabilitiesOpen += amount;
        }
      });
    }

    return {
      receivablesTotal,
      receivablesOpen,
      receivablesOverdue,
      liabilitiesTotal,
      liabilitiesOpen,
      liabilitiesOverdue,
    };
  }, [invoicesData]);

  const isLoading = loading || companyLoading;

  return (
    <MainLayout>
      <div className="flex flex-col h-full overflow-auto bg-muted/30">
        <div className="flex-1 p-6 lg:p-8 space-y-8">
          {/* Header */}
          <div className="space-y-3">
            <h1 className="text-4xl font-bold tracking-tight bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text">
              Dashboard
            </h1>
          </div>

          <DashboardFilters
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
          />

          {error && (
            <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 shadow-sm">
              <p className="text-sm text-destructive font-medium">
                ⚠️ Fehler beim Laden der Dashboard-Daten. Bitte versuchen Sie es
                erneut.
              </p>
            </div>
          )}

          {isLoading && (
            <div className="space-y-8">
              <Skeleton className="h-[500px] w-full rounded-lg" />
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Skeleton className="h-[500px] rounded-lg" />
                <Skeleton className="h-[500px] rounded-lg" />
              </div>
            </div>
          )}

          {/* Charts */}
          {!isLoading && dateRange && (
            <div className="space-y-8">
              <IncomeExpenseChart
                accountsData={timeSeriesData}
                counterpartiesData={timeSeriesCounterpartiesData}
                currency="EUR"
                granularity={granularity}
                onGranularityChange={setGranularity}
              />

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <TopCustomersChart data={topCustomersData} currency="EUR" />
                <TopSuppliersChart data={topSuppliersData} currency="EUR" />
              </div>
            </div>
          )}

          {/* Empty State */}
          {!isLoading && !error && dateRange && !aggregations && (
            <div className="flex flex-col items-center justify-center h-[400px] gap-4 text-muted-foreground bg-card rounded-lg border shadow-sm">
              <div className="text-6xl">📊</div>
              <div className="text-center">
                <h3 className="text-xl font-semibold mb-2 text-foreground">
                  Keine Daten verfügbar
                </h3>
                <p className="text-sm max-w-md">
                  Es wurden keine Buchungen für den ausgewählten Zeitraum
                  gefunden. Ändern Sie die Filter oder fügen Sie neue Buchungen
                  hinzu.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
}

DashboardPage.displayName = "DashboardPage";
